// Comprehensive Terms of Service & Privacy Policy Page Component (/terms)
// Hosts Platform Scope, Community Privacy Policy, and Full Event Media Release
import type { TermsConfig } from '../types';
import { getCachedTermsConfig } from '../services/termsService';
import { navigateTo } from '../router';
import { getCurrentProfile } from '../services/authService';

export function renderTermsPage(customTerms?: TermsConfig, isUserAuthenticated: boolean = false): string {
  const terms = customTerms || getCachedTermsConfig();

  return `
    <div class="min-h-screen flex flex-col bg-[#06070a] text-slate-100 font-sans selection:bg-cyan-400 selection:text-slate-950">
      
      <!-- Sticky Navigation Header -->
      <header class="bg-[#06070a]/95 border-b border-white/[0.08] sticky top-0 z-40 backdrop-blur-md sticky-header">
        <div class="laser-line-cyan absolute bottom-0 left-0 right-0 opacity-40"></div>
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          
          <div class="flex items-center gap-3 cursor-pointer" id="terms-logo-link">
            <div class="w-15 h-15 rounded-xl flex items-center justify-center p-0 hover:shadow-[0_0_18px_rgba(0,240,255,0.5)] transition-all">
                <img src="/logo.png" alt="Pops Culture Logo" class="w-full h-full object-contain">
              </div>
            <div>
              <div class="flex items-center gap-2">
                <span class="text-lg font-black tracking-wider uppercase"><span class="text-[#FF2A85]">Pops</span> <span class="text-[#00f0ff]">Culture</span></span>
              </div>
              <p class="text-[11px] text-slate-400 hidden sm:block">Full Platform Terms of Service & Privacy Policy</p>
            </div>
          </div>

          <div class="flex items-center gap-3">
            ${isUserAuthenticated ? `
              <button id="terms-back-app-btn" class="btn-primary px-4 py-2 rounded-xl text-xs flex items-center gap-2 font-bold cursor-pointer">
                <i class="fa-solid fa-compass"></i>
                <span>Go to App</span>
              </button>
            ` : `
              <button id="terms-back-home-btn" class="btn-secondary px-4 py-2 rounded-xl text-xs flex items-center gap-2 font-bold cursor-pointer">
                <i class="fa-solid fa-arrow-left"></i>
                <span>Back to Home</span>
              </button>
            `}
          </div>

        </div>
      </header>

      <!-- Main Terms & Privacy Portal Content -->
      <main class="flex-1 py-10 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto w-full space-y-8">
        
        <!-- Header Hero Banner -->
        <div class="card-workshop p-6 sm:p-8 bg-[#090b12] border-cyan-400/30 relative overflow-hidden space-y-4 shadow-[0_0_30px_rgba(0,240,255,0.1)]">
          <div class="laser-line-cyan absolute top-0 left-0 right-0"></div>
          
          <div class="flex items-center gap-3">
            <div class="w-12 h-12 rounded-xl bg-cyan-950/40 border border-cyan-400/50 text-cyan-400 flex items-center justify-center font-bold text-xl shadow-[0_0_14px_rgba(0,240,255,0.25)] flex-shrink-0">
              <i class="fa-solid fa-scale-balanced"></i>
            </div>
            <div>
              <div class="flex items-center gap-2 flex-wrap">
                <h1 class="text-xl sm:text-2xl font-black text-slate-100 tracking-tight">Terms of Service & Privacy Policy</h1>
                <span class="text-xs font-mono text-cyan-400 border border-cyan-400/50 px-2 py-0.5 rounded shadow-[0_0_8px_rgba(0,240,255,0.2)]">v${terms.version}</span>
              </div>
              <p class="text-xs text-slate-400 mt-0.5">Effective Date: ${terms.lastUpdated} &bull; Official Community Operating Scope</p>
            </div>
          </div>

          <p class="text-xs sm:text-sm text-slate-300 leading-relaxed">
            Welcome to Pops Culture. Below is the complete list of our <strong>Terms of Service</strong>, <strong>Community Privacy Policy</strong>, and <strong>Event Media Release</strong>. These guidelines ensure our shoulder-to-shoulder gatherings remain safe, transparent, and trustworthy.
          </p>

          <!-- Quick Navigation Jump Pills -->
          <div class="pt-2 flex items-center gap-2 flex-wrap border-t border-white/[0.08] text-xs">
            <span class="text-slate-400 font-bold uppercase text-[10px] tracking-wider mr-1">Jump To:</span>
            <a href="#terms-scope" class="px-3 py-1.5 rounded-lg bg-[#06070a] border border-white/[0.1] hover:border-cyan-400 text-cyan-300 hover:text-white transition-colors flex items-center gap-1.5 font-semibold">
              <i class="fa-solid fa-shield-halved text-cyan-400"></i> Platform Scope & Terms
            </a>
            <a href="#privacy-policy" class="px-3 py-1.5 rounded-lg bg-[#06070a] border border-white/[0.1] hover:border-emerald-400 text-emerald-300 hover:text-white transition-colors flex items-center gap-1.5 font-semibold">
              <i class="fa-solid fa-user-lock text-emerald-400"></i> Privacy Policy
            </a>
            <a href="#media-release" class="px-3 py-1.5 rounded-lg bg-[#06070a] border border-white/[0.1] hover:border-pink-500 text-pink-300 hover:text-white transition-colors flex items-center gap-1.5 font-semibold">
              <i class="fa-solid fa-camera text-pink-400"></i> Event Media Release
            </a>
          </div>
        </div>

        <!-- SECTION 1: PLATFORM SCOPE & TERMS OF SERVICE -->
        <section id="terms-scope" class="space-y-4">
          <div class="flex items-center gap-2.5 pb-2 border-b border-cyan-400/30">
            <i class="fa-solid fa-shield-halved text-cyan-400 text-lg"></i>
            <h2 class="text-lg font-black text-slate-100 uppercase tracking-wider">
              Part I: Platform Scope & Terms of Service
            </h2>
          </div>

          <!-- Summary Callout -->
          <div class="p-4 bg-[#090b12] rounded-xl border border-white/[0.08] text-slate-300 text-xs sm:text-sm leading-relaxed flex items-start gap-3">
            <i class="fa-solid fa-circle-info text-cyan-400 text-base mt-0.5 flex-shrink-0"></i>
            <div>
              <strong class="text-slate-100 block mb-0.5">Platform Noticeboard Scope:</strong>
              ${terms.summary}
            </div>
          </div>

          <div class="space-y-4">
            <div class="card-workshop p-5 sm:p-6 bg-[#090b12] border-white/[0.08] space-y-2">
              <h3 class="font-bold text-cyan-300 text-sm sm:text-base uppercase tracking-wider flex items-center gap-2">
                <span class="text-cyan-400 font-mono text-xs border border-cyan-400/30 px-1.5 py-0.5 rounded">01</span>
                Facilitator Status & Independent Adult Assembly
              </h3>
              <p class="text-xs sm:text-sm text-slate-300 leading-relaxed pt-1">
                Pops Culture is strictly a communications and mutual discovery software application designed to help adult fathers discover common volunteer and neighborly DIY interests. The platform facilitates introductions and discussion only. We are not a general contractor, construction manager, architectural service, or credentialing authority.
              </p>
            </div>

            <div class="card-workshop p-5 sm:p-6 bg-[#090b12] border-white/[0.08] space-y-2">
              <h3 class="font-bold text-cyan-300 text-sm sm:text-base uppercase tracking-wider flex items-center gap-2">
                <span class="text-cyan-400 font-mono text-xs border border-cyan-400/30 px-1.5 py-0.5 rounded">02</span>
                Complete Absence of Credentialing
              </h3>
              <p class="text-xs sm:text-sm text-slate-300 leading-relaxed pt-1">
                Pops Culture does not vet, license, verify, background-check, or authenticate any user's skills, trade certifications, or safety history. Any skill badges represent self-declared amateur hobbies and peer interests, not warrantied vocational capability.
              </p>
            </div>

            <div class="card-workshop p-5 sm:p-6 bg-[#090b12] border-white/[0.08] space-y-2">
              <h3 class="font-bold text-cyan-300 text-sm sm:text-base uppercase tracking-wider flex items-center gap-2">
                <span class="text-cyan-400 font-mono text-xs border border-cyan-400/30 px-1.5 py-0.5 rounded">03</span>
                Permits, HOA, Zoning & Code Compliance
              </h3>
              <p class="text-xs sm:text-sm text-slate-300 leading-relaxed pt-1">
                Event hosts bear 100% sole legal responsibility for verifying and obtaining all requisite municipal building permits, structural inspections, HOA approvals, and utility markouts (e.g., 811 Call Before You Dig). Pops Culture accepts zero responsibility for unpermitted structures or municipal code fines.
              </p>
            </div>

            <div class="card-workshop p-5 sm:p-6 bg-[#090b12] border-white/[0.08] space-y-2">
              <h3 class="font-bold text-cyan-300 text-sm sm:text-base uppercase tracking-wider flex items-center gap-2">
                <span class="text-cyan-400 font-mono text-xs border border-cyan-400/30 px-1.5 py-0.5 rounded">04</span>
                Assumption of Risk & Personal Protective Equipment (PPE)
              </h3>
              <p class="text-xs sm:text-sm text-slate-300 leading-relaxed pt-1">
                Working with construction tools, heavy materials, lumber, and power equipment carries inherent physical risks. Every attendee is an independent volunteer who assumes all personal risk of property damage or physical injury. Users are strictly responsible for assessing their own capabilities and bringing proper personal protective equipment (safety glasses, hearing protection, work boots).
              </p>
            </div>

            <div class="card-workshop p-5 sm:p-6 bg-[#090b12] border-white/[0.08] space-y-2">
              <h3 class="font-bold text-cyan-300 text-sm sm:text-base uppercase tracking-wider flex items-center gap-2">
                <span class="text-cyan-400 font-mono text-xs border border-cyan-400/30 px-1.5 py-0.5 rounded">05</span>
                Child Privacy & Safety Protocol
              </h3>
              <p class="text-xs sm:text-sm text-slate-300 leading-relaxed pt-1">
                Family squad profiles display only broad age brackets and general recreational interests (e.g., "Son (8) - Little League") to help fathers identify common playdate opportunities. No full names, schools, birthdates, or identifying photos of minors are required or displayed publicly.
              </p>
            </div>
          </div>
        </section>

        <!-- SECTION 2: COMMUNITY PRIVACY POLICY -->
        <section id="privacy-policy" class="space-y-4 pt-4">
          <div class="flex items-center gap-2.5 pb-2 border-b border-emerald-400/30">
            <i class="fa-solid fa-user-lock text-emerald-400 text-lg"></i>
            <h2 class="text-lg font-black text-slate-100 uppercase tracking-wider">
              Part II: Community Privacy Policy
            </h2>
          </div>

          <div class="card-workshop p-6 sm:p-7 bg-[#090b12] border-emerald-400/20 space-y-4">
            <h3 class="text-sm sm:text-base font-bold text-emerald-300 uppercase tracking-wider flex items-center gap-2">
              <i class="fa-solid fa-clipboard-check text-emerald-400"></i> Community Privacy Summary
            </h3>

            <div class="space-y-3 text-xs sm:text-sm text-slate-300 leading-relaxed">
              <div class="p-3.5 rounded-xl bg-[#06070a] border border-white/[0.08] space-y-1">
                <strong class="text-slate-100 flex items-center gap-1.5 text-xs uppercase tracking-wider text-emerald-400">
                  <i class="fa-solid fa-calendar-check"></i> Information Used Strictly for Events
                </strong>
                <p class="text-slate-300">
                  We collect only what is necessary to coordinate meetups—such as your name, contact details, neighborhood, and optional event-specific notes (e.g., child age groups or severe allergies for safety and logistics).
                </p>
              </div>

              <div class="p-3.5 rounded-xl bg-[#06070a] border border-white/[0.08] space-y-1">
                <strong class="text-slate-100 flex items-center gap-1.5 text-xs uppercase tracking-wider text-emerald-400">
                  <i class="fa-solid fa-user-shield"></i> Parents Are in Control
                </strong>
                <p class="text-slate-300">
                  Our platform is designed strictly for adult parents and legal guardians (18+). Children do not create accounts, and parents or guardians may review, update, or request the immediate deletion of their family’s data at any time.
                </p>
              </div>

              <div class="p-3.5 rounded-xl bg-[#06070a] border border-white/[0.08] space-y-1">
                <strong class="text-slate-100 flex items-center gap-1.5 text-xs uppercase tracking-wider text-emerald-400">
                  <i class="fa-solid fa-camera"></i> Event Media & Community Photos
                </strong>
                <p class="text-slate-300">
                  When you host or attend an event, photos and video may be captured and shared within our community platform and promotional materials. Our comprehensive media terms and photo takedown procedures are outlined in detail below.
                </p>
              </div>

              <div class="p-3.5 rounded-xl bg-[#06070a] border border-white/[0.08] space-y-1">
                <strong class="text-slate-100 flex items-center gap-1.5 text-xs uppercase tracking-wider text-emerald-400">
                  <i class="fa-solid fa-lock"></i> Secure & Minimal Retention
                </strong>
                <p class="text-slate-300">
                  We protect all user information using industry-standard security protocols and encryption, limit data access to event organizers strictly on an as-needed operational basis, and purge temporary data periodically.
                </p>
              </div>

              <div class="p-3.5 rounded-xl bg-[#06070a] border border-white/[0.08] space-y-1">
                <strong class="text-slate-100 flex items-center gap-1.5 text-xs uppercase tracking-wider text-emerald-400">
                  <i class="fa-solid fa-hand-holding-dollar"></i> No Broker Sales; Ad-Supported Operation
                </strong>
                <p class="text-slate-300">
                  We never sell, rent, or trade your contact info, registration lists, or personal details to data brokers or direct marketers. To keep our community running, we display ads via third-party vendors such as Google AdSense. These third-party vendors may use cookies or other trackers to serve relevant ads based on browsing activity. You can control personalized advertising at any time through the controls provided by those platforms directly.
                </p>
              </div>
            </div>
          </div>
        </section>

        <!-- SECTION 3: FULL MEDIA, PHOTOGRAPHY, AND PUBLICITY RELEASE -->
        <section id="media-release" class="space-y-4 pt-4">
          <div class="flex items-center gap-2.5 pb-2 border-b border-pink-500/30">
            <i class="fa-solid fa-camera text-pink-400 text-lg"></i>
            <h2 class="text-lg font-black text-slate-100 uppercase tracking-wider">
              Part III: Full Media, Photography, and Publicity Release
            </h2>
          </div>

          <div class="space-y-4">
            
            <div class="card-workshop p-5 sm:p-6 bg-[#090b12] border-white/[0.08] space-y-2">
              <h3 class="font-bold text-pink-400 text-sm sm:text-base uppercase tracking-wider flex items-center gap-2">
                <span class="text-pink-400 font-mono text-xs border border-pink-500/30 px-1.5 py-0.5 rounded">01</span>
                Grant of Rights and License
              </h3>
              <p class="text-xs sm:text-sm text-slate-300 leading-relaxed pt-1">
                By registering for, hosting, or attending an event organized through <strong>Pops Culture</strong> (the "Platform"), you ("User") grant to the Platform, its affiliates, legal representatives, successors, and assigns, a perpetual, worldwide, non-exclusive, royalty-free, fully paid-up, and sublicensable license to:
              </p>
              <ul class="list-disc list-inside space-y-1 text-xs sm:text-sm text-slate-400 pl-2 leading-relaxed">
                <li>Photograph, film, record, and capture your image, likeness, voice, name, biographical details, and appearance (collectively, "Likeness") during the event;</li>
                <li>Reproduce, publish, stream, distribute, publicly display, edit, alter, composite, and otherwise use such recordings and photographs in any and all media now known or hereafter devised; and</li>
                <li>Use such materials for any lawful purpose, including platform features, member-facing community boards, promotional campaigns, social media, public relations, digital advertising, and marketing collateral, without geographic or temporal restriction.</li>
              </ul>
            </div>

            <div class="card-workshop p-5 sm:p-6 bg-[#090b12] border-white/[0.08] space-y-2">
              <h3 class="font-bold text-pink-400 text-sm sm:text-base uppercase tracking-wider flex items-center gap-2">
                <span class="text-pink-400 font-mono text-xs border border-pink-500/30 px-1.5 py-0.5 rounded">02</span>
                Representation of Authority Regarding Minors and Guests
              </h3>
              <p class="text-xs sm:text-sm text-slate-300 leading-relaxed pt-1">
                If you create an event, RSVP, or bring any additional individuals (including adult guests, spouses, domestic partners, and minor children) to an event, you explicitly represent, warrant, and certify that:
              </p>
              <div class="space-y-2 text-xs sm:text-sm text-slate-400 pl-2 leading-relaxed">
                <p>
                  <strong class="text-slate-200">Minor Children:</strong> You are the parent or court-appointed legal guardian of any minor child accompanying you, or you have secured written, legally binding authorization and consent from the child’s legal parent or guardian permitting the Platform to capture and use the child’s Likeness under the exact terms of this Release.
                </p>
                <p>
                  <strong class="text-slate-200">Adult Guests:</strong> You have notified every adult guest attending under your invitation or registration of this Release, and you have obtained their express permission to bind them to these terms.
                </p>
                <p>
                  <strong class="text-slate-200">Authority to Bind:</strong> You possess full legal power and authority to enter into this Release on behalf of yourself and all individuals included in your party.
                </p>
              </div>
            </div>

            <div class="card-workshop p-5 sm:p-6 bg-[#090b12] border-white/[0.08] space-y-2">
              <h3 class="font-bold text-pink-400 text-sm sm:text-base uppercase tracking-wider flex items-center gap-2">
                <span class="text-pink-400 font-mono text-xs border border-pink-500/30 px-1.5 py-0.5 rounded">03</span>
                Indemnification
              </h3>
              <p class="text-xs sm:text-sm text-slate-300 leading-relaxed pt-1">
                You agree to defend, indemnify, and hold harmless the Platform, its organizers, officers, directors, contractors, agents, and community hosts from and against any and all claims, demands, liabilities, damages, costs, and expenses (including reasonable attorneys' fees) arising out of or related to:
              </p>
              <ul class="list-disc list-inside space-y-1 text-xs sm:text-sm text-slate-400 pl-2 leading-relaxed">
                <li>Any claim that an individual or minor brought or invited by you did not authorize the capture, use, or publication of their Likeness;</li>
                <li>Any breach of your representations and warranties regarding guest or parental authority under Section 2; or</li>
                <li>Any assertion of rights of privacy, publicity, defamation, or commercial misappropriation asserted by you or any member of your party.</li>
              </ul>
            </div>

            <div class="card-workshop p-5 sm:p-6 bg-[#090b12] border-white/[0.08] space-y-2">
              <h3 class="font-bold text-pink-400 text-sm sm:text-base uppercase tracking-wider flex items-center gap-2">
                <span class="text-pink-400 font-mono text-xs border border-pink-500/30 px-1.5 py-0.5 rounded">04</span>
                Waiver of Inspection and Compensation
              </h3>
              <p class="text-xs sm:text-sm text-slate-300 leading-relaxed pt-1">
                You and all members of your attendee party waive:
              </p>
              <ul class="list-disc list-inside space-y-1 text-xs sm:text-sm text-slate-400 pl-2 leading-relaxed">
                <li>Any right to inspect, approve, or pre-screen the finished photographs, videos, audio recordings, or promotional materials in which your or their Likeness appears;</li>
                <li>Any claim to financial compensation, royalties, residuals, or other proceeds arising from the exploitation, distribution, or publication of such media; and</li>
                <li>Any legal action, cause of action, or tort claim for invasion of privacy, false light, infringement of the statutory or common-law right of publicity, copyright infringement, or defamation arising out of the reasonable use, cropping, and editing of such recordings.</li>
              </ul>
            </div>

            <div class="card-workshop p-5 sm:p-6 bg-[#090b12] border-white/[0.08] space-y-2">
              <h3 class="font-bold text-pink-400 text-sm sm:text-base uppercase tracking-wider flex items-center gap-2">
                <span class="text-pink-400 font-mono text-xs border border-pink-500/30 px-1.5 py-0.5 rounded">05</span>
                Takedown Policy and Revocation Requests
              </h3>
              <p class="text-xs sm:text-sm text-slate-300 leading-relaxed pt-1">
                While this license is granted on a royalty-free and irrevocable basis for past publications, the Platform values family trust and privacy. If you or a member of your party wish to request the removal of a specific, published photograph or video featuring your or your dependent’s identifiable Likeness from the Platform's website or digital marketing channels, you may submit a written notice containing a link to the specific asset to <a href="mailto:support@popsculture.dad" class="text-cyan-400 underline font-bold hover:text-cyan-300">support@popsculture.dad</a>.
              </p>
              <p class="text-xs sm:text-sm text-slate-400 leading-relaxed">
                The Platform will make commercially reasonable efforts to remove, blur, or de-index the identified individual from digital properties under its direct operational control within a reasonable administrative period, provided that such accommodation shall not apply to previously distributed, printed, or archived third-party promotional materials.
              </p>
            </div>

          </div>
        </section>

        <!-- Bottom Contact & Questions Box -->
        <div class="p-6 rounded-2xl bg-[#090b12] border border-white/[0.08] text-xs text-slate-400 space-y-2.5">
          <div class="font-bold text-slate-200 uppercase tracking-wider text-[11px] text-cyan-400 flex items-center gap-2">
            <i class="fa-solid fa-envelope"></i> Questions, Takedown Notices & Community Inquiries
          </div>
          <p class="leading-relaxed">
            For trust, safety, media takedown requests, or privacy questions, please contact our team directly at:
            <br>
            <a href="mailto:support@popsculture.dad" class="text-cyan-400 underline font-bold hover:text-cyan-300 text-xs">support@popsculture.dad</a>
          </p>
        </div>

        <!-- Bottom Return Actions -->
        <div class="pt-4 flex items-center justify-between border-t border-white/[0.08] text-xs">
          <button id="terms-bottom-return-btn" class="btn-secondary px-5 py-2.5 rounded-xl font-bold flex items-center gap-2 cursor-pointer">
            <i class="fa-solid fa-arrow-left"></i>
            <span>${isUserAuthenticated ? 'Return to App' : 'Return to Home'}</span>
          </button>
          <span class="text-[11px] text-slate-500">Pops Culture &copy; 2026. Full Terms of Service & Privacy Policy.</span>
        </div>

      </main>

      <!-- Persistent Footer -->
      <footer class="bg-[#06070a] border-t border-white/[0.08] text-slate-400 text-xs py-8 px-4 sm:px-6 lg:px-8 mt-12 relative">
        <div class="laser-line-cyan absolute top-0 left-0 right-0 opacity-20"></div>
        <div class="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-[11px] text-slate-500">
          <div>
            <span class="font-bold text-slate-300">Pops Culture</span> &copy; 2026. Shoulder-to-Shoulder Community.
          </div>
          <div class="flex items-center gap-4">
            <button id="footer-home-link" class="hover:text-cyan-400 transition-colors cursor-pointer">Home</button>
            <span class="text-slate-600">&bull;</span>
            <span class="text-slate-400 font-semibold">Terms of Service & Privacy Policy</span>
            <span class="text-slate-600">&bull;</span>
            <a href="mailto:support@popsculture.dad" class="hover:text-cyan-400 transition-colors">Support</a>
          </div>
        </div>
      </footer>

    </div>
  `;
}

export function bindTermsPageEvents(): void {
  const isAuth = Boolean(getCurrentProfile());
  const returnTarget = isAuth ? '/app' : '/';

  const navigateBack = () => navigateTo(returnTarget);

  document.getElementById('terms-logo-link')?.addEventListener('click', () => navigateTo('/'));
  document.getElementById('terms-back-home-btn')?.addEventListener('click', () => navigateTo('/'));
  document.getElementById('terms-back-app-btn')?.addEventListener('click', () => navigateTo('/app'));
  document.getElementById('terms-bottom-return-btn')?.addEventListener('click', navigateBack);
  document.getElementById('footer-home-link')?.addEventListener('click', () => navigateTo('/'));
}
